import { useEffect, useState, useMemo } from 'react';
import { useAuth } from '@/context/AuthContext';
import HexagonLoader from '@/components/HexagonLoader/HexagonLoader';
import {
  PieChart, Pie, Cell, Tooltip, ResponsiveContainer, Legend,
  BarChart, Bar, XAxis, YAxis, CartesianGrid
} from 'recharts';
import { usePrintMode } from './PrintModeContext';
import * as Sentry from '@sentry/react';

const COLORS = ['#2e7d32', '#1976d2', '#ed6c02', '#d32f2f', '#8e24aa', '#455a64', '#00897b', '#5d4037'];
const STATUS_COLORS = {
  online: '#2e7d32',
  degraded: '#ed6c02',
  offline: '#d32f2f',
  unmonitored: '#9e9e9e'
};

export default function RouterFleetSummary() {
  const { apiFetch } = useAuth();
  const isPrintMode = usePrintMode();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const resp = await apiFetch('/api/RouterConfig/fleet-summary?days=30');
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const json = await resp.json();
        setData(json.data || json);
      } catch (e) {
          Sentry.captureException(e);
        setError(e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, []); // eslint-disable-line

  // Health score: weighted from online%, provisioned%, monitoring coverage
  const healthScore = useMemo(() => {
    if (!data || !data.totalRouters) return null;
    const total = data.totalRouters;
    const onlinePct = (data.online / Math.max(1, total - data.unmonitored)) * 100;
    const provisionedPct = data.provisioningDistribution
      ? (data.provisioningDistribution.find(p => p.status === 'Provisioned')?.count || 0) / total * 100
      : 0;
    const noOffline = data.offline === 0 ? 100 : Math.max(0, 100 - data.offline / total * 200);
    return Math.round(onlinePct * 0.5 + provisionedPct * 0.3 + noOffline * 0.2);
  }, [data]);

  const getScoreInterpretation = (score) => {
    if (score == null) return { text: 'Insufficient Data', color: '#999', icon: '?' };
    if (score >= 90) return { text: 'Excellent', color: '#2e7d32', icon: '✓' };
    if (score >= 75) return { text: 'Good', color: '#7cb342', icon: '✓' };
    if (score >= 60) return { text: 'Fair', color: '#ffa000', icon: '⚠' };
    if (score >= 40) return { text: 'Needs Attention', color: '#ff5722', icon: '⚠' };
    return { text: 'Critical', color: '#d32f2f', icon: '✗' };
  };

  const statusData = useMemo(() => {
    if (!data) return [];
    return [
      { name: 'Online', value: data.online || 0 },
      { name: 'Degraded', value: data.degraded || 0 },
      { name: 'Offline', value: data.offline || 0 },
      { name: 'Unmonitored', value: data.unmonitored || 0 }
    ].filter(d => d.value > 0);
  }, [data]);

  const firmwareData = useMemo(() => {
    if (!data?.firmwareDistribution) return [];
    return data.firmwareDistribution.map(f => ({
      name: f.version,
      count: f.count
    }));
  }, [data]);

  const locationData = useMemo(() => {
    if (!data?.locationDistribution) return [];
    return data.locationDistribution.slice(0, 10);
  }, [data]);

  if (loading) return <HexagonLoader />;
  if (error) return <div style={{ color: 'red' }}>{error}</div>;
  if (!data || !data.totalRouters) return null;

  const scoreInterp = getScoreInterpretation(healthScore);
  const statusColorMap = ['#2e7d32', '#ed6c02', '#d32f2f', '#9e9e9e'];

  // Identify routers that may need firmware updates (not on latest version)
  const latestFirmware = firmwareData.length > 0 ? firmwareData[0].name : null;
  const outdatedCount = latestFirmware
    ? firmwareData.filter(f => f.name !== latestFirmware).reduce((sum, f) => sum + f.count, 0)
    : 0;

  const Progress = ({ percent, color = '#1976d2' }) => (
    <div style={{ background: '#eee', height: 6, borderRadius: 6 }}>
      <div style={{
        width: `${Math.min(100, Math.max(0, percent))}%`,
        height: '100%',
        background: color,
        borderRadius: 6
      }} />
    </div>
  );

  const llmProxyText = [
    `Router Fleet Health Score: ${healthScore}/100`,
    `Total Routers: ${data.totalRouters}`,
    `Online: ${data.online}, Offline: ${data.offline}, Degraded: ${data.degraded}`,
    `Backups (30d): ${data.backupsLast30Days}, Config Changes: ${data.configChangesLast30Days}`
  ].join('\n');

  return (
    <div className="dashboard-card group-wrapper router-fleet-summary">
      <div data-llm-proxy style={{ display:'none' }}>{llmProxyText}</div>
      <h2 style={{ marginTop:0, marginBottom: 12 }}>MikroTik Router Fleet Summary</h2>

      {/* Executive Summary Banner */}
      <div className="dashboard-card exec-banner" style={{ borderLeft: `4px solid ${scoreInterp.color}` }}>
        <div className="exec-banner-layout">
          <div className="exec-banner-score">
            <div className="exec-banner-value" style={{ color: scoreInterp.color }}>
              {healthScore != null ? healthScore : '—'}
              <span className="exec-banner-suffix">/ 100</span>
            </div>
            <div className="exec-banner-label" style={{ color: scoreInterp.color }}>{scoreInterp.icon} {scoreInterp.text}</div>
            <div className="exec-banner-sublabel">Fleet Health</div>
          </div>
          <div className="exec-banner-body">
            <div className="exec-banner-text">
              <strong>What this means:</strong> Your managed router fleet of <strong>{data.totalRouters} routers</strong> has <strong>{data.online} online</strong>{data.offline > 0 && <>, <span style={{ color: '#d32f2f', fontWeight: 600 }}>{data.offline} offline</span></>}{data.degraded > 0 && <>, <span style={{ color: '#ed6c02', fontWeight: 600 }}>{data.degraded} degraded</span></>}.
              {data.offline === 0 && data.degraded === 0 && ' All routers are operating normally.'}
              {data.offline > 0 && <span style={{ color: '#d32f2f', fontWeight: 600 }}> Offline routers require immediate attention.</span>}
              {outdatedCount > 0 && <span style={{ color: '#ed6c02', fontWeight: 600 }}> {outdatedCount} router{outdatedCount > 1 ? 's' : ''} not on latest firmware.</span>}
            </div>
          </div>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="metrics-row">
        <div className="dashboard-card metric-card"><div className="metric-label">Total Routers</div><div className="metric-value">{data.totalRouters}</div></div>
        <div className="dashboard-card metric-card"><div className="metric-label">Online</div><div className="metric-value" style={{ color: '#2e7d32' }}>{data.online}</div></div>
        <div className="dashboard-card metric-card"><div className="metric-label">Offline</div><div className="metric-value" style={{ color: data.offline > 0 ? '#d32f2f' : '#2e7d32' }}>{data.offline}</div></div>
        <div className="dashboard-card metric-card"><div className="metric-label">Degraded</div><div className="metric-value" style={{ color: data.degraded > 0 ? '#ed6c02' : '#2e7d32' }}>{data.degraded}</div></div>
        <div className="dashboard-card metric-card"><div className="metric-label">Monitored IPs</div><div className="metric-value">{data.monitoredPublicIps}</div></div>
        <div className="dashboard-card metric-card"><div className="metric-label">Backups (30d)</div><div className="metric-value">{data.backupsLast30Days}</div></div>
        <div className="dashboard-card metric-card"><div className="metric-label">Config Changes</div><div className="metric-value">{data.configChangesLast30Days}</div></div>
        <div className="dashboard-card metric-card">
          <div className="metric-label">Firmware Versions</div>
          <div className="metric-value" style={{ color: outdatedCount > 0 ? '#ed6c02' : '#2e7d32' }}>{firmwareData.length}</div>
          <div className="metric-subtitle">{outdatedCount > 0 ? `${outdatedCount} not on latest` : 'All current'}</div>
        </div>
      </div>

      {/* Charts row */}
      <div style={{ display: 'grid', gridTemplateColumns: isPrintMode ? '330px 330px' : '1fr 1fr', gap: 20, marginBottom: 20 }}>
        {/* Status distribution pie */}
        <div style={{ background: '#fafbfc', borderRadius: 8, padding: 16, border: '1px solid #eee' }}>
          <h4 style={{ margin: '0 0 8px', fontSize: '0.85rem', color: '#555' }}>Router Status Distribution</h4>
          <ResponsiveContainer width="100%" height={200}>
            <PieChart>
              <Pie
                data={statusData}
                cx="50%"
                cy="50%"
                innerRadius={50}
                outerRadius={80}
                paddingAngle={2}
                dataKey="value"
                isAnimationActive={!isPrintMode}
              >
                {statusData.map((_, i) => (
                  <Cell key={i} fill={statusColorMap[i % statusColorMap.length]} />
                ))}
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Firmware distribution bar chart */}
        {firmwareData.length > 0 && (
          <div style={{ background: '#fafbfc', borderRadius: 8, padding: 16, border: '1px solid #eee' }}>
            <h4 style={{ margin: '0 0 8px', fontSize: '0.85rem', color: '#555' }}>RouterOS Firmware Versions</h4>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={firmwareData} layout="vertical" margin={{ left: 20 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" allowDecimals={false} />
                <YAxis type="category" dataKey="name" width={80} tick={{ fontSize: 11 }} />
                <Tooltip />
                <Bar dataKey="count" fill="#1976d2" radius={[0, 4, 4, 0]} isAnimationActive={!isPrintMode} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {/* Provisioning & Location breakdown */}
      <div style={{ display: 'grid', gridTemplateColumns: isPrintMode ? '330px 330px' : '1fr 1fr', gap: 20, marginBottom: 16 }}>
        {/* Provisioning status */}
        {data.provisioningDistribution?.length > 0 && (
          <div style={{ background: '#fafbfc', borderRadius: 8, padding: 16, border: '1px solid #eee' }}>
            <h4 style={{ margin: '0 0 12px', fontSize: '0.85rem', color: '#555' }}>Provisioning Status</h4>
            {data.provisioningDistribution.map((p) => {
              const pct = (p.count / data.totalRouters * 100).toFixed(0);
              const color = p.status === 'Provisioned' ? '#2e7d32' : p.status === 'Failed' ? '#d32f2f' : '#1976d2';
              return (
                <div key={p.status} style={{ marginBottom: 10 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 3 }}>
                    <span style={{ fontWeight: 600 }}>{p.status}</span>
                    <span style={{ color: '#888' }}>{p.count} ({pct}%)</span>
                  </div>
                  <Progress percent={parseFloat(pct)} color={color} />
                </div>
              );
            })}
          </div>
        )}

        {/* Location distribution */}
        {locationData.length > 0 && (
          <div style={{ background: '#fafbfc', borderRadius: 8, padding: 16, border: '1px solid #eee' }}>
            <h4 style={{ margin: '0 0 12px', fontSize: '0.85rem', color: '#555' }}>Routers by Location</h4>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={locationData} layout="vertical" margin={{ left: 10 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" allowDecimals={false} />
                <YAxis type="category" dataKey="location" width={100} tick={{ fontSize: 10 }} />
                <Tooltip />
                <Bar dataKey="count" fill="#00897b" radius={[0, 4, 4, 0]} isAnimationActive={!isPrintMode} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {/* Per-router status table */}
      {data.routers?.length > 0 && (
        <div style={{ overflowX: 'auto' }}>
          <h4 style={{ margin: '0 0 8px', fontSize: '0.85rem', color: '#555' }}>Individual Router Status</h4>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #ddd', textAlign: 'left' }}>
                <th style={{ padding: '6px 8px' }}>Router</th>
                <th style={{ padding: '6px 8px' }}>Location</th>
                <th style={{ padding: '6px 8px' }}>Status</th>
                <th style={{ padding: '6px 8px' }}>Firmware</th>
                <th style={{ padding: '6px 8px' }}>Provisioning</th>
                <th style={{ padding: '6px 8px' }}>Last Seen</th>
                <th style={{ padding: '6px 8px' }}>Alerts</th>
              </tr>
            </thead>
            <tbody>
              {data.routers.sort((a, b) => {
                const order = { offline: 0, degraded: 1, online: 2, unmonitored: 3 };
                return (order[a.status] ?? 9) - (order[b.status] ?? 9);
              }).map(r => (
                <tr key={r.id} style={{ borderBottom: '1px solid #eee' }}>
                  <td style={{ padding: '5px 8px', fontWeight: 600 }}>{r.name}</td>
                  <td style={{ padding: '5px 8px', color: '#666' }}>{r.location || '—'}</td>
                  <td style={{ padding: '5px 8px' }}>
                    <span style={{
                      display: 'inline-block',
                      padding: '2px 8px',
                      borderRadius: 10,
                      fontSize: 10,
                      fontWeight: 700,
                      background: `${STATUS_COLORS[r.status] || '#999'}18`,
                      color: STATUS_COLORS[r.status] || '#999',
                      textTransform: 'uppercase',
                      letterSpacing: '0.03em'
                    }}>
                      {r.status}
                    </span>
                  </td>
                  <td style={{ padding: '5px 8px', fontFamily: 'monospace', fontSize: 11 }}>
                    {r.firmware || '—'}
                  </td>
                  <td style={{ padding: '5px 8px', fontSize: 11 }}>{r.provisioning}</td>
                  <td style={{ padding: '5px 8px', fontSize: 11, color: '#888' }}>
                    {r.lastSeen ? new Date(r.lastSeen).toLocaleString() : '—'}
                  </td>
                  <td style={{ padding: '5px 8px', textAlign: 'center' }}>
                    {r.alertCount > 0 ? (
                      <span style={{
                        background: '#d32f2f',
                        color: '#fff',
                        borderRadius: '50%',
                        width: 20,
                        height: 20,
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 10,
                        fontWeight: 700
                      }}>{r.alertCount}</span>
                    ) : (
                      <span style={{ color: '#ccc' }}>—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
